/**
 * Which call, if any, a job being written belongs to.
 *
 * The job form used to latch the first live call it saw while mounted, full
 * stop, and `useActiveCall()` is not scoped to anything. So a CSR who answered
 * any call with the form open stamped that call onto whatever job they saved
 * next, for whoever it was for — and on the call report that reads as a
 * booking the call never produced.
 *
 * The caller's contact is kept alongside the id so the link can still be
 * judged after the call ends, when the live call is gone and there is nothing
 * left to compare against.
 */
export interface JobCallLink {
  id: string;
  /** The contact the caller matched when they rang. Null for a stranger. */
  contactId: string | null;
}

/**
 * True when a live call could plausibly have produced this job.
 *
 * The whole rule, in two lines, over the only two things known: who the caller
 * matched, and who the job is for.
 *
 * A stranger's call linked to a job that already names someone used to pass
 * here, which left the ambient-stamping bug alive for exactly the callers most
 * likely to hit it: answer a new number, save a job for an existing customer,
 * and the call was counted as that job's booking. It cannot be that customer.
 * Caller-id had their number and matched nobody, so whoever rang is not them.
 *
 * The conversion this used to protect is not lost, because it never looks like
 * that: a stranger who becomes a customer is created by the form's inline
 * panel at submit, and `form.contactId` stays empty until then (the form
 * tracks that path as `inlineCustomerReady` instead). So the valuable case is
 * always "stranger, job has nobody yet", which still links.
 *
 * A stranger ringing ON BEHALF of an existing customer — a tenant about to be
 * booked under their landlord — loses the link. That is deliberate: it is
 * indistinguishable from the bug, and an invented booking corrupts the number
 * the report exists to give, while a missed one only understates it.
 */
export function callBelongsToJob(
  callContactId: string | null,
  jobContactId: string | null,
): boolean {
  // Nobody on the job yet: any caller could still turn out to be its customer,
  // including the stranger the CSR is about to create.
  if (!jobContactId) return true;
  // The job names someone, so only the person who rang can have produced it.
  return callContactId === jobContactId;
}

/**
 * The link a job should carry, given what it holds and what is on the phone.
 *
 * One rule, asked of each candidate in preference order: the link already held
 * if it still describes this job, else the one on the phone if it does, else
 * none. Re-testing what is held is what makes a link survive the hangup
 * without surviving the job moving to someone else — the caller's contact is
 * remembered, so there is still something to judge against.
 *
 * The two steps are separate on purpose. Falling through to `incoming` only
 * when the held link has stopped belonging is what covers the handover: a
 * stale call held from earlier, and a live one that matches the customer now
 * chosen. Stopping at the held link — as this did while the candidates were
 * collapsed to `current ?? incoming` — withdrew the stale link and never
 * adopted the valid one, which is the same under-count for job-first bookings
 * from the other direction.
 *
 * Held wins over live when both belong, so a link does not skip between calls
 * once made.
 */
export function nextJobCallLink(
  current: JobCallLink | undefined,
  incoming: JobCallLink | null,
  jobContactId: string | null,
): JobCallLink | undefined {
  if (current && callBelongsToJob(current.contactId, jobContactId)) return current;
  if (incoming && callBelongsToJob(incoming.contactId, jobContactId)) return incoming;
  return undefined;
}
